import { useState, useEffect, useCallback } from 'react';
import { getCursosConEventos, putEventoYCurso } from '../../../services/evento.service';
import { getPerfiles } from '../../../services/perfiles.service';
import { getAreasTematicas } from '../../../services/areasTematicas.service';
import { getTiposCertificaciones } from '../../../services/tiposCertificaciones.service';
import { getPlataformasDictado } from '../../../services/plataformasDictado.service';
import { getMediosInscripcion } from '../../../services/mediosInscripcion.service';
import { getTiposCapacitacion } from '../../../services/tiposCapacitacion.service';
import { getAreas } from '../../../services/areas.service';
import { getMinisterios } from '../../../services/ministerios.service';
import { putCurso } from '../../../services/cursos.service';

const useEventoYCurso = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Datos auxiliares del Evento
    const [perfiles, setPerfiles] = useState([]);
    const [areasTematicas, setAreasTematicas] = useState([]);
    const [tiposCertificacion, setTiposCertificacion] = useState([]);

    // Datos auxiliares del Curso
    const [plataformas, setPlataformas] = useState([]);
    const [medios, setMedios] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [areas, setAreas] = useState([]);
    const [ministerios, setMinisterios] = useState([]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [
                cursosConEventosRes,
                perfilesRes,
                areasTematicasRes,
                tiposCertificacionRes,
                plataformasRes,
                mediosRes,
                tiposRes,
                areasRes,
                ministeriosRes
            ] = await Promise.all([
                getCursosConEventos(),
                getPerfiles(),
                getAreasTematicas(),
                getTiposCertificaciones(),
                getPlataformasDictado(),
                getMediosInscripcion(),
                getTiposCapacitacion(),
                getAreas(),
                getMinisterios().catch((err) => {
                    console.warn("No se pudieron cargar los ministerios:", err);
                    return [];
                })
            ]);

            setData(cursosConEventosRes);
            setPerfiles(perfilesRes);
            setAreasTematicas(areasTematicasRes);
            setTiposCertificacion(tiposCertificacionRes);
            setPlataformas(plataformasRes);
            setMedios(mediosRes);
            setTipos(tiposRes);
            setAreas(areasRes);
            setMinisterios(ministeriosRes || []);
            setError(null);
        } catch (err) {
            console.error("Error fetching Cursos con Eventos data:", err);
            setError(err.message || "Error al cargar los datos.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Actualiza un curso y/o su evento asociado mediante el endpoint PUT /eventos/:curso.
     * Si el curso no posee evento y se completaron los datos, el backend realiza un Upsert
     * creando el evento atómicamente y sin alterar el estado del curso.
     */
    const updateItem = async (item) => {
        setLoading(true);
        try {
            const tieneEvento = item.tieneEvento;
            const tieneNuevosDatosEvento = Boolean(
                item.perfil || item.area_tematica || item.tipo_certificacion ||
                (item.presentacion && item.presentacion.trim())
            );

            if (!tieneEvento && !tieneNuevosDatosEvento) {
                // Solo se actualizan datos del curso mediante PUT /cursos
                const cursoPayload = {
                    cod: item.curso,
                    nombre: item.nombre,
                    cupo: parseInt(item.cupo, 10),
                    cantidad_horas: parseInt(item.cantidad_horas, 10),
                    medio_inscripcion: item.codMedioInscripcion,
                    plataforma_dictado: item.codPlataformaDictado,
                    tipo_capacitacion: item.codTipoCapacitacion,
                    area: item.codArea || null,
                    numero_evento: item.numero_evento !== '' && item.numero_evento != null ? parseInt(item.numero_evento, 10) : null,
                    aplica_sincronizacion_certificados: item.aplica_sincronizacion_certificados ? 1 : 0,
                    url_curso: item.url_curso || null,
                    ...(item.esVigente !== undefined ? { esVigente: item.esVigente } : {}),
                    ...(item.tiene_evento_creado !== undefined ? { tiene_evento_creado: item.tiene_evento_creado } : {}),
                    ...(item.esta_maquetado !== undefined ? { esta_maquetado: item.esta_maquetado } : {}),
                    ...(item.esta_configurado !== undefined ? { esta_configurado: item.esta_configurado } : {}),
                    ...(item.esta_autorizado !== undefined ? { esta_autorizado: item.esta_autorizado } : {}),
                    ...(item.tiene_formulario_evento_creado !== undefined ? { tiene_formulario_evento_creado: item.tiene_formulario_evento_creado } : {}),
                };
                await putCurso(cursoPayload);
            } else {
                const payload = {
                    // Campos del Curso
                    curso: item.curso,
                    nombre: item.nombre,
                    cupo: parseInt(item.cupo, 10),
                    cantidad_horas: parseInt(item.cantidad_horas, 10),
                    medio_inscripcion: item.codMedioInscripcion,
                    plataforma_dictado: item.codPlataformaDictado,
                    tipo_capacitacion: item.codTipoCapacitacion,
                    area: item.codArea || null,
                    esVigente: item.esVigente,
                    tiene_evento_creado: item.tiene_evento_creado,
                    numero_evento: item.numero_evento !== '' && item.numero_evento != null ? parseInt(item.numero_evento, 10) : null,
                    esta_maquetado: item.esta_maquetado,
                    esta_configurado: item.esta_configurado,
                    aplica_sincronizacion_certificados: item.aplica_sincronizacion_certificados,
                    url_curso: item.url_curso,
                    esta_autorizado: item.esta_autorizado,
                    // Campos de Evento
                    perfil: item.perfil || undefined,
                    area_tematica: item.area_tematica || undefined,
                    tipo_certificacion: item.tipo_certificacion || undefined,
                    presentacion: item.presentacion || undefined,
                    objetivos: item.objetivos || undefined,
                    requisitos_aprobacion: item.requisitos_aprobacion || undefined,
                    ejes_tematicos: item.ejes_tematicos || undefined,
                    certifica_en_cc: item.certifica_en_cc,
                    disenio_a_cargo_cc: item.disenio_a_cargo_cc,
                };
                await putEventoYCurso(payload);
            }
            await fetchData();
            return { success: true };
        } catch (err) {
            setError(err.message || "Error al actualizar.");
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };



    /**
     * Cambia el estado de un curso usando el endpoint PUT /cursos,
     * que actualiza datos y estado en una sola transacción atómica.
     * @param {Object} record - Registro completo del curso
     * @param {string} accion - 'avanzar' | 'retroceder' | 'darDeBaja' | 'restaurar'
     * @param {string|null} estadoDestino - Solo para acción 'restaurar'
     */
    const changeEstado = async (record, accion, estadoDestino = null) => {
        setLoading(true);
        try {
            const payload = {
                cod: record.cod,
                nombre: record.nombre,
                cupo: record.cupo,
                cantidad_horas: record.cantidad_horas,
                medio_inscripcion: record.medio_inscripcion,
                plataforma_dictado: record.plataforma_dictado,
                tipo_capacitacion: record.tipo_capacitacion,
                area: record.area,
                aplica_sincronizacion_certificados: record.aplica_sincronizacion_certificados,
                url_curso: record.url_curso || null,
                numero_evento: record.numero_evento || null,
                accion,
                ...(estadoDestino ? { estadoDestino } : {})
            };
            await putCurso(payload);
            await fetchData();
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    };

    return {
        data,
        loading,
        error,
        updateItem,
        changeEstado,
        refreshData: fetchData,
        auxiliaryData: {
            // Evento
            perfiles,
            areasTematicas,
            tiposCertificacion,
            // Curso
            plataformas,
            medios,
            tipos,
            areas,
            ministerios
        }
    };
};

export default useEventoYCurso;
